import { pluck, shaker } from './synth.js';

/**
 * Light 16-step pentatonic loop (tempo 96) with two alternating 2-bar
 * variations, scheduled with a 120 ms lookahead so it never drifts against
 * the audio clock. Triangle pluck + shaker; the engine ducks it on blasts.
 */

// A minor pentatonic across ~2 octaves (Hz).
const SCALE = [220, 261.63, 293.66, 329.63, 392, 440, 523.25, 587.33];

// 16 steps/bar × 4 bars before looping (2 bars per variation).
// -1 = rest. Indices into SCALE.
const BARS: number[][] = [
  [0, -1, 4, -1, 2, -1, 4, 5, 3, -1, 2, -1, 0, -1, -1, -1],
  [-1, 5, -1, 4, -1, 3, -1, 2, 4, -1, 5, -1, 6, -1, 5, -1],
  [4, -1, 2, -1, 5, -1, 4, -1, 3, -1, 0, -1, 2, -1, -1, -1],
  [-1, 3, -1, 5, -1, 6, -1, 5, 4, -1, 3, -1, 2, -1, 4, 5],
];
// Bass pulse on beats 1 and 3 of each bar (SCALE index -2 = octave down).
const BASS_STEPS = [0, 8];

export class MusicLoop {
  private timer: number | null = null;
  private nextStepTime = 0;
  private step = 0;
  private playing = false;
  /** Everything plays through here so duck() can ramp one param. */
  private readonly gain: GainNode;

  constructor(
    private readonly ctx: AudioContext,
    dest: AudioNode,
    /** Seconds per 16th note at tempo 96. */
    private readonly stepDur = 60 / 96 / 4,
  ) {
    this.gain = ctx.createGain();
    this.gain.gain.value = 1;
    this.gain.connect(dest);
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  start(): void {
    if (this.playing) return;
    this.playing = true;
    this.nextStepTime = this.ctx.currentTime + 0.1;
    this.timer = window.setInterval(() => this.schedule(), 60);
  }

  stop(): void {
    this.playing = false;
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** Duck the music (0 = silent, 1 = full) with a short ramp. */
  duck(amount: number, rampS = 0.1): void {
    const t = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(t);
    this.gain.gain.setTargetAtTime(amount, t, rampS / 3);
    this.gain.gain.setTargetAtTime(1, t + rampS + 0.25, 0.4); // recover
  }

  private schedule(): void {
    // Queue notes up to 200 ms ahead so short timer jitter never gaps.
    while (this.nextStepTime < this.ctx.currentTime + 0.2) {
      this.emit(this.nextStepTime, this.step);
      this.nextStepTime += this.stepDur;
      this.step = (this.step + 1) % 64; // 4 bars × 16
    }
  }

  private emit(t: number, step: number): void {
    const bar = Math.floor(step / 16);
    const i = step % 16;
    const notes = BARS[bar];

    const n = notes[i];
    if (n >= 0) {
      pluck(this.ctx, this.gain, t, SCALE[n], 0.5, 0.16);
      // Octave sparkle on accented downbeats.
      if (i % 8 === 0) pluck(this.ctx, this.gain, t, SCALE[Math.min(SCALE.length - 1, n + 4)], 0.35, 0.07);
    }
    if (BASS_STEPS.includes(i)) {
      pluck(this.ctx, this.gain, t, SCALE[0] / 2, 0.4, 0.13);
    }
    if (i % 4 === 2) shaker(this.ctx, this.gain, t, 0.045);
    if (i % 8 === 6) shaker(this.ctx, this.gain, t + this.stepDur / 2, 0.03);
  }
}
